import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EyeIcon, DocumentArrowDownIcon, EnvelopeIcon, CreditCardIcon, ShieldCheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import FcfaIcon from '../components/FcfaIcon';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useMailing } from '../contexts/MailingContext';
import { useToast } from '../contexts/ToastContext';
import { usePaginatedApi } from '../hooks/useApi';
import { billsAPI, clientsAPI, paymentsAPI } from '../services/api';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';

import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchableSelect from '../components/SearchableSelect';
import { paymentVerifierDisplayName } from '../utils/helpers';
import { getBillPaymentStatusChipsSorted, paymentStatusI18nKey } from '../utils/paymentStatusI18n';

const defaultBillFilters = () => ({
  search: '',
  clientId: '',
  status: '',
  startDate: '',
  endDate: ''
});

const BillsPage = () => {
  const { t, i18n } = useTranslation();
  const billPdfLang = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
  const location = useLocation();
  const navigate = useNavigate();
  const openBillFromNavHandledRef = useRef(null);
  const { isOperator, isSuper, isAccountant, isClient, isPeseur } = useAuth();
  const { mailingEnabled } = useMailing();
  const { success, error } = useToast();

  // Filters — seed clientId from navigation state so the first list request matches the filter (avoids racing the mount fetch vs. the nav effect).
  const [filters, setFilters] = useState(() => {
    const base = defaultBillFilters();
    const cid = location.state?.filterClientId;
    if (cid != null && String(cid) !== '') {
      return { ...base, clientId: String(cid) };
    }
    return base;
  });
  const [showPaidVerifiedBills, setShowPaidVerifiedBills] = useState(false);
  const [billFiltersOpen, setBillFiltersOpen] = useState(() => {
    const cid = location.state?.filterClientId;
    return cid != null && String(cid) !== '';
  });

  const withListParams = useCallback((base, paidVerifiedOverride) => {
    const want = typeof paidVerifiedOverride === 'boolean' ? paidVerifiedOverride : showPaidVerifiedBills;
    return { ...base, includePaidVerified: want ? 'true' : 'false' };
  }, [showPaidVerifiedBills]);

  // Modal states
  const [selectedBill, setSelectedBill] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [showMarkPaidVerifiedModal, setShowMarkPaidVerifiedModal] = useState(false);
  const [billForMarkPaid, setBillForMarkPaid] = useState(null);
  const [billMarkPaidDetail, setBillMarkPaidDetail] = useState(null);
  const [markPaidDetailLoading, setMarkPaidDetailLoading] = useState(false);
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);
  const [markPaidSettlementMethod, setMarkPaidSettlementMethod] = useState('CASH');
  /** When bill was opened from a certificate, keep id for load-detail return navigation. */
  const [linkedCertificateId, setLinkedCertificateId] = useState(null);

  // API hook for bills
  const {
    data: bills,
    pagination,
    loading: billsLoading,
    error: billsError,
    updateParams,
    goToPage,
    refetch: refetchBills
  } = usePaginatedApi(billsAPI.getAll, filters);

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const skipInitialSearchDebounceRef = useRef(true);
  const searchDebounceRef = useRef(null);
  useEffect(() => {
    if (skipInitialSearchDebounceRef.current) {
      skipInitialSearchDebounceRef.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      updateParams(withListParams({ ...filtersRef.current }));
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [filters.search, updateParams, withListParams]);

  // Open bill modal and/or set client filter when navigating (notifications, client view, etc.)
  useEffect(() => {
    const openBillId = location.state?.openBillId;
    const openCertificateId = location.state?.openCertificateId;
    const filterClientId = location.state?.filterClientId;
    const hasFilter =
      filterClientId != null && filterClientId !== '';
    if (!openBillId && !hasFilter) {
      openBillFromNavHandledRef.current = null;
      setLinkedCertificateId(null);
      return;
    }
    const dedupeKey = `${openBillId ?? ''}|${filterClientId ?? ''}`;
    if (openBillFromNavHandledRef.current === dedupeKey) return;
    openBillFromNavHandledRef.current = dedupeKey;

    navigate(location.pathname, { replace: true, state: {} });

    if (hasFilter) {
      const cid = String(filterClientId);
      setFilters((prev) => ({ ...prev, clientId: cid }));
      if (filters.clientId !== cid) {
        updateParams(
          withListParams({
            ...filters,
            clientId: cid
          })
        );
      }
    }

    if (openCertificateId) {
      setLinkedCertificateId(String(openCertificateId));
    }

    if (openBillId) {
      (async () => {
        try {
          setIsLoading(true);
          const response = await billsAPI.getById(openBillId);
          setSelectedBill(response.data.bill);
          setShowBillModal(true);
        } catch (err) {
          console.error('Error opening bill from notification:', err);
          error(err.response?.data?.message || err.message || t('bills.errorOpeningBill'));
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [
    location.state?.openBillId,
    location.state?.openCertificateId,
    location.state?.filterClientId,
    location.pathname,
    navigate,
    t,
    error,
    updateParams,
    showPaidVerifiedBills,
    withListParams,
    filters
  ]);

  // Peseur: clients from visible bills (includes ponctuel / PONCTUEL). Others: full client list.
  const billClientFilterApi = isPeseur() ? billsAPI.getFilterClients : clientsAPI.getAll;
  const billClientFilterParams = isPeseur() ? {} : { limit: 100 };
  const { data: clientsData, loading: clientsLoading } = usePaginatedApi(
    billClientFilterApi,
    billClientFilterParams
  );

  const clientFilterOptions = useMemo(
    () => {
      const clients = Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []);
      return [
        { value: '', label: t('certificates.allClients') },
        ...clients.map((c) => ({ value: c.id, label: c.companyName || '—' })),
      ];
    },
    [clientsData, t]
  );

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-update for non-search fields, but not for search
    if (field !== 'search') {
      updateParams(withListParams({
        ...filters,
        [field]: value
      }));
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      search: '',
      clientId: '',
      status: '',
      startDate: '',
      endDate: ''
    };
    setFilters(clearedFilters);
    setShowPaidVerifiedBills(false);
    updateParams(withListParams(clearedFilters, false));
  };

  const handleShowPaidVerifiedBillsChange = (e) => {
    const checked = Boolean(e.target.checked);
    setShowPaidVerifiedBills(checked);
    updateParams(withListParams({ ...filters }, checked));
  };

  const handleViewBill = async (bill) => {
    try {
      setIsLoading(true);
      // Fetch full bill details including loads
      const response = await billsAPI.getById(bill.id);
      setSelectedBill(response.data.bill);
      setShowBillModal(true);
    } catch (error) {
      console.error('Error fetching bill details:', error);
      // Fallback to using the bill from the list if API call fails
      setSelectedBill(bill);
      setShowBillModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayBill = (bill) => {
    setSelectedBill(bill);
    setShowPaymentModal(true);
  };

  const handleOpenRelatedPayment = (paymentId) => {
    if (!paymentId) return;
    setShowBillModal(false);
    navigate('/payments', { state: { openPaymentId: paymentId } });
  };

  const handleOpenLoadFromBill = (load) => {
    const loadId = load?.id || selectedBill?.loadId;
    if (!loadId) return;
    setShowBillModal(false);
    const navState = { openLoadId: loadId };
    if (linkedCertificateId) {
      navState.openCertificateId = linkedCertificateId;
    }
    if (selectedBill?.id) {
      navState.openBillId = selectedBill.id;
    }
    navigate('/loads', navState);
  };

  const handleDownloadPDF = async (billId, billNumber) => {
    try {
      setIsLoading(true);
      const response = await billsAPI.getPDF(billId, billPdfLang);

      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const newWin = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWin) {
        error(t('bills.popupBlockedForPdf'));
        window.URL.revokeObjectURL(url);
        return;
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      success(t('bills.pdfDownloaded'));
    } catch (err) {
      console.error('Error downloading PDF:', err);
      const errorMessage = err.response?.data?.message || err.message || t('bills.errorDownloadingPdf');
      error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const canAccountantMarkPaidVerified = (bill) =>
    bill && bill.status !== 'PAID' && bill.status !== 'CANCELLED' && (
      isAccountant() || (isSuper() && bill.billKind === 'CREDIT_TOPUP')
    );

  const closeMarkPaidVerifiedModal = useCallback(() => {
    setShowMarkPaidVerifiedModal(false);
    setBillForMarkPaid(null);
    setBillMarkPaidDetail(null);
    setMarkPaidSettlementMethod('CASH');
  }, []);

  const paymentMethodLabel = useCallback(
    (method) => {
      if (!method) return '—';
      const k = String(method).toLowerCase().replace(/_/g, '');
      return t(`payments.methods.${k}`, { defaultValue: method });
    },
    [t]
  );

  const openMarkPaidVerifiedModal = async (bill) => {
    if (!canAccountantMarkPaidVerified(bill)) return;
    setBillForMarkPaid(bill);
    setBillMarkPaidDetail(null);
    setMarkPaidSettlementMethod('CASH');
    setShowMarkPaidVerifiedModal(true);
    setMarkPaidDetailLoading(true);
    try {
      const res = await billsAPI.getById(bill.id);
      setBillMarkPaidDetail(res.data.bill);
    } catch (e) {
      console.error('Error loading bill for mark paid:', e);
      setBillMarkPaidDetail(bill);
    } finally {
      setMarkPaidDetailLoading(false);
    }
  };

  const executeMarkPaidVerified = async () => {
    const id = billForMarkPaid?.id;
    if (!id) return;
    try {
      setMarkPaidSubmitting(true);
      await billsAPI.markPaidVerified(id, { settlementPaymentMethod: markPaidSettlementMethod });
      success(t('bills.markPaidVerifiedSuccess'));
      closeMarkPaidVerifiedModal();
      refetchBills();
      if (selectedBill?.id === id) {
        try {
          const res = await billsAPI.getById(id);
          setSelectedBill(res.data.bill);
        } catch (_) {
          setSelectedBill(null);
          setShowBillModal(false);
        }
      }
    } catch (err) {
      console.error('Mark paid verified error:', err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t('bills.markPaidVerifiedError');
      error(msg);
    } finally {
      setMarkPaidSubmitting(false);
    }
  };

  const handleSendEmail = async (billId) => {
    try {
      setIsLoading(true);
      await billsAPI.sendEmail(billId, { lang: billPdfLang });
      success(t('billSentSuccessfully'));
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.message || err.message || t('errorSendingEmail');
      error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getBillStatusLabel = useCallback((bill, paymentChips = null) => {
    const status = String(bill?.status || '').toUpperCase();
    if (!status) return '-';
    if (status !== 'PAID') {
      return t(`bills.${status.toLowerCase()}`, { defaultValue: status });
    }
    const chips = Array.isArray(paymentChips) ? paymentChips : getBillPaymentStatusChipsSorted(bill || {});
    const hasWaitingVerification = chips.some((chip) => chip.status === 'WAITING_FOR_VERIFICATION');
    return hasWaitingVerification
      ? t('bills.paid')
      : t('bills.paidVerified', { defaultValue: t('bills.paid') });
  }, [t]);

  const formatFcfa = (amount) => {
    const value = Math.round(Number(amount || 0));
    return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA`;
  };

  const columns = [
    {
      key: 'billNumber',
      title: t('bills.billNumber'),
      render: (value) => (
        <span className="font-mono text-sm">{value}</span>
      )
    },
    // Client column - Hidden only for client users (single assigned client)
    ...(!isClient() ? [{
      key: 'client',
      title: t('client'),
      render: (value, row) => row.client?.companyName || '-'
    }] : []),

    {
      key: 'date',
      title: t('date'),
      render: (value) => format(new Date(value), 'dd/MM/yyyy')
    },
    {
      key: 'dueDate',
      title: t('bills.dueDate'),
      render: (value) => format(new Date(value), 'dd/MM/yyyy')
    },
    {
      key: 'totalAmount',
      title: t('amount'),
      render: (value) => (
        <span className="font-semibold">
          {new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF'
          }).format(value)}
        </span>
      )
    },
    {
      key: 'status',
      title: t('status'),
      render: (value, row) => {
        const paymentChips = getBillPaymentStatusChipsSorted(row);

        const chipClass = (status) => {
          if (status === 'FAILED') return 'bg-red-100 text-red-800';
          if (status === 'WAITING_FOR_VERIFICATION') return 'bg-orange-100 text-orange-800';
          if (status === 'PENDING') return 'bg-yellow-100 text-yellow-800';
          return 'bg-gray-100 text-gray-800';
        };

        const chipLabel = (chip) => {
          if (chip.status === 'FAILED') return t('bills.paymentRejectedBadge');
          return t(paymentStatusI18nKey(chip.payment, row.payments), {
            defaultValue: chip.payment.status
          });
        };

        return (
          <div className="space-y-1">
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(value)}`}>
              {getBillStatusLabel(row, paymentChips)}
            </span>
            {paymentChips.map((chip) => (
              <div key={chip.key} className="text-xs">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded ${chipClass(chip.status)}`}
                >
                  {chipLabel(chip)}
                </span>
              </div>
            ))}
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: t('actions'),
      sticky: true,
      width: '260px',
      minWidth: 260,
      render: (_, row) => (
        <div className="flex flex-nowrap items-center gap-1 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewBill(row)}
            className="p-1"
          >
            <EyeIcon className="h-4 w-4" />
          </Button>
          {canAccountantMarkPaidVerified(row) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openMarkPaidVerifiedModal(row)}
              className="p-1 text-emerald-600 hover:text-emerald-700"
              disabled={isLoading}
              title={t('bills.markPaidVerified')}
            >
              <ShieldCheckIcon className="h-4 w-4" />
            </Button>
          )}
          {/* Pay Bill button - For clients on unpaid bills (pending/overdue) without waiting payments */}
          {isOperator() && (row.status === 'PENDING' || row.status === 'OVERDUE') && (
            (() => {
              const hasWaitingPayments = row.payments?.some(p => p.status === 'WAITING_FOR_VERIFICATION') || false;
              return !hasWaitingPayments ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePayBill(row)}
                  className="p-1 text-green-600 hover:text-green-700"
                  disabled={isLoading}
                  title={t('payments.payBill')}
                >
                  <CreditCardIcon className="h-4 w-4" />
                </Button>
              ) : null;
            })()
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownloadPDF(row.id, row.billNumber)}
            className="p-1"
            disabled={isLoading}
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
          </Button>
          {mailingEnabled && !isSuper() && !isPeseur() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSendEmail(row.id)}
              className="p-1"
              disabled={isLoading}
            >
              <EnvelopeIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      )
    }
  ];



  if (billsError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <FcfaIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isClient() ? t('bills.titleClient') : t('bills.title')}
            </h1>
            <p className="text-gray-600">{t('bills.billsDescription')}</p>
          </div>
        </div>
        
        <div className="card">
          <div className="text-center text-red-600">
            <p>{t('bills.errorLoadingBills')}</p>
            <Button onClick={refetchBills} className="mt-2">
              {t('reports.retry')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <FcfaIcon className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isClient() ? t('bills.titleClient') : t('bills.title')}
          </h1>
          <p className="text-gray-600">
            {isPeseur() ? t('bills.peseurBillsDescription') : t('bills.billsDescription')}
          </p>
        </div>
      </div>

      {/* Search — debounced; updates list automatically */}
      <div className="card">
        <Input
          placeholder={t('bills.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="w-full"
        />
      </div>

      {/* Filters — collapsible */}
      <div className="card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-medium text-gray-900">{t('reports.filters')}</h3>
          <Button
            type="button"
            variant="outline"
            className="shrink-0 inline-flex items-center justify-center gap-2"
            onClick={() => setBillFiltersOpen((o) => !o)}
            aria-expanded={billFiltersOpen}
            aria-controls="bills-filters-panel"
          >
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-600 transition-transform ${billFiltersOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
            {billFiltersOpen ? t('bills.hideBillFilters') : t('bills.showBillFilters')}
          </Button>
        </div>
        {billFiltersOpen && (
          <div id="bills-filters-panel" className="mt-4 space-y-4">
            <div className={`grid grid-cols-1 md:grid-cols-2 ${isClient() ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4`}>
              {/* Client filter - Hidden only for client users */}
              {!isClient() && (
                <SearchableSelect
                  label={t('client')}
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                  options={clientFilterOptions}
                  placeholder={t('certificates.allClients')}
                  disabled={clientsLoading}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('status')}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('bills.allStatuses')}</option>
                  <option value="PENDING">{t('bills.pending')}</option>
                  <option value="PAID">{t('bills.paid')}</option>
                  <option value="OVERDUE">{t('bills.overdue')}</option>
                  <option value="CANCELLED">{t('bills.cancelled')}</option>
                </select>
              </div>

              <Input
                type="date"
                label={t('reports.startDate')}
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />

              <Input
                type="date"
                label={t('reports.endDate')}
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>

            <div className="pt-4 border-t border-gray-100">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="checkbox-app"
                  checked={showPaidVerifiedBills}
                  onChange={handleShowPaidVerifiedBillsChange}
                />
                <span className="text-sm text-gray-800">{t('bills.showPaidVerifiedBills')}</span>
              </label>
            </div>

            <div>
              <Button variant="outline" onClick={clearFilters}>
                {t('reports.clearFilters')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bills Table */}
      <div className="card">
        {billsLoading ? (
          <LoadingSpinner />
        ) : (
          <Table
            columns={columns}
            data={bills || []}
            pagination={pagination}
            onPageChange={(page) => goToPage(page)}
          />
        )}
      </div>

      {/* Bill Details Modal */}
      {selectedBill && (
        <Modal
          isOpen={showBillModal}
          onClose={() => {
            setShowBillModal(false);
            setLinkedCertificateId(null);
          }}
          title={`${t('bills.billDetails')} - ${selectedBill.billNumber}`}
          maxWidth="4xl"
        >
          <div className="space-y-6">
            {(() => {
              const primaryLoad = selectedBill.loads?.[0];
              const primaryTicketNumber = primaryLoad?.ticket?.ticketNumber;
              let totalMontantHt = Number(
                selectedBill.totalMontantHt ??
                selectedBill.loads?.reduce((sum, load) => sum + (Number(load.amountHtExclTva ?? load.amountHt - (load.amountTva || 0)) || 0), 0) ??
                0
              );
              let totalTva = Number(
                selectedBill.totalTva ??
                selectedBill.loads?.reduce((sum, load) => sum + (Number(load.amountTva) || 0), 0) ??
                0
              );
              let totalMontantTtc = Number(
                selectedBill.totalMontantTtc ??
                selectedBill.loads?.reduce((sum, load) => sum + (Number(load.amountTtc) || 0), 0) ??
                0
              );
              const storedBillTtc = Number(selectedBill.totalAmount) || 0;
              if (totalMontantTtc <= 0 && storedBillTtc > 0) {
                totalMontantTtc = storedBillTtc;
                const tvaFraction = 0.18 / 1.18;
                totalTva = Math.round(totalMontantTtc * tvaFraction);
                totalMontantHt = totalMontantTtc - totalTva;
              }

              return (
                <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('bills.billInformation')}</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>{t('bills.billNumber')}:</strong> {selectedBill.billNumber}</div>
                  <div><strong>{t('date')}:</strong> {format(new Date(selectedBill.date), 'dd/MM/yyyy')}</div>
                  <div><strong>{t('bills.dueDate')}:</strong> {format(new Date(selectedBill.dueDate), 'dd/MM/yyyy')}</div>
                  <div>
                    <strong>{t('status')}:</strong>
                    <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(selectedBill.status)}`}>
                      {getBillStatusLabel(selectedBill)}
                    </span>
                  </div>
                  <div>
                    <strong>{t('amount')}:</strong>
                    <span className="ml-2 text-lg font-semibold text-green-600">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'XOF'
                      }).format(selectedBill.totalAmount)}
                    </span>
                  </div>
                  {(primaryLoad?.id || selectedBill.loadId) && (
                    <div>
                      <strong>{t('loads.ticketNumber')}:</strong>{' '}
                      {primaryTicketNumber ? (
                        <button
                          type="button"
                          className="ml-1 font-mono text-blue-700 hover:text-blue-900 underline underline-offset-2"
                          onClick={() => handleOpenLoadFromBill(primaryLoad || { id: selectedBill.loadId })}
                        >
                          {primaryTicketNumber}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ml-1 text-blue-700 hover:text-blue-900 underline underline-offset-2"
                          onClick={() => handleOpenLoadFromBill(primaryLoad || { id: selectedBill.loadId })}
                        >
                          {t('bills.viewWeighingLoad')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('bills.clientInformation')}</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong>{t('clients.companyName')}:</strong>{' '}
                    {selectedBill.client?.id ? (
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                        onClick={() => {
                          setShowBillModal(false);
                          navigate('/clients', { state: { openClientId: selectedBill.client.id } });
                        }}
                      >
                        {selectedBill.client.companyName}
                      </button>
                    ) : (
                      selectedBill.client.companyName
                    )}
                  </div>
                  <div><strong>{t('clients.contactName')}:</strong> {selectedBill.client.contactName}</div>
                  <div><strong>{t('clients.contactEmail')}:</strong> {selectedBill.client.contactEmail}</div>
                </div>
              </div>
            </div>
            
            {selectedBill.certificate && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('bills.certificateInformation')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <strong>{t('certificates.certificateNumber')}:</strong>{' '}
                    {selectedBill.certificate?.id ? (
                      <button
                        type="button"
                        className="font-mono underline underline-offset-2 hover:text-blue-700"
                        onClick={() => {
                          setShowBillModal(false);
                          navigate('/certificates', { state: { openCertificateId: selectedBill.certificate.id } });
                        }}
                      >
                        {selectedBill.certificate.certificateNumber}
                      </button>
                    ) : (
                      <span className="font-mono">{selectedBill.certificate.certificateNumber}</span>
                    )}
                  </div>
                  <div>
                    <strong>{t('loads.modal.ddpNumber')}:</strong>{' '}
                    {selectedBill.certificate?.ddpNumber ||
                      selectedBill.loads?.find((l) => l.ddp?.manutentionNumber)?.ddp?.manutentionNumber ||
                      '-'}
                  </div>
                  <div><strong>{t('certificates.product')}:</strong> {selectedBill.certificate.product?.name}</div>
                  <div><strong>{t('certificates.totalLoads')}:</strong> {selectedBill.certificate.totalLoads}</div>
                  <div><strong>{t('certificates.totalWeight')}:</strong> {(selectedBill.certificate.totalWeight / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Ton</div>
                </div>
              </div>
            )}

            {selectedBill.loads && selectedBill.loads.length > 0 && (
              <div className={selectedBill.certificate ? 'mt-4' : ''}>
                <h5 className="font-medium text-gray-900 mb-3">
                  {selectedBill.certificate
                    ? t('certificates.loadDetails')
                    : t('bills.weighingLoadDetails')}
                </h5>
                <div className="space-y-3">
                  {selectedBill.loads.map((load) => (
                        <div
                          key={load.id}
                          className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-2 hover:shadow-md transition-shadow"
                        >
                          {/* Header Row */}
                          <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                {t('loads.ticketNumber')}:
                              </span>
                              {load.ticket?.ticketNumber ? (
                                <button
                                  type="button"
                                  className="text-sm font-mono font-medium text-blue-700 hover:text-blue-900 underline underline-offset-2"
                                  onClick={() => handleOpenLoadFromBill(load)}
                                >
                                  {load.ticket.ticketNumber}
                                </button>
                              ) : (
                                <span className="text-sm font-mono font-medium text-gray-900">-</span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">
                              {format(new Date(load.loadDate), 'dd/MM/yyyy HH:mm')}
                            </span>
                          </div>

                          {/* Main Content Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Truck Information */}
                            <div className="space-y-1">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                {t('tables.truck')}
                              </div>
                              <div className="text-sm text-gray-900">
                                <div className="font-medium">{load.truck?.plateNumber || '-'}</div>
                                {load.truck?.driverName && (
                                  <div className="text-xs text-gray-500">{load.truck.driverName}</div>
                                )}
                              </div>
                            </div>

                            {/* Weighbridge Information */}
                            <div className="space-y-1">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                {t('weighing.weighbridge')}
                              </div>
                              <div className="text-sm text-gray-900">
                                <div>{load.weighbridge?.name || '-'}</div>
                                {load.weighbridge?.location && (
                                  <div className="text-xs text-gray-500">{load.weighbridge.location}</div>
                                )}
                              </div>
                            </div>

                            {/* Port Information */}
                            <div className="space-y-1">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                {t('weighing.port')}
                              </div>
                              <div className="text-sm text-gray-900">
                                <div>{load.port?.name || '-'}</div>
                                {load.port?.location && (
                                  <div className="text-xs text-gray-500">{load.port.location}</div>
                                )}
                              </div>
                            </div>

                            {/* Weights Section */}
                            <div className="space-y-2">
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">{t('weighing.emptyWeight')}</div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {load.emptyWeight !== null && load.emptyWeight !== undefined ? (load.emptyWeight / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : '-'} Ton
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">{t('weighing.loadedWeight')}</div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {load.loadedWeight !== null && load.loadedWeight !== undefined ? (load.loadedWeight / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : '-'} Ton
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">{t('weighing.netWeight')}</div>
                                  <div className="text-sm font-medium text-blue-600">
                                    {load.netWeight !== null && load.netWeight !== undefined ? (load.netWeight / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 }) : '-'} Ton
                                  </div>
                                </div>
                              </div>
                              <div className="pt-2 border-t border-gray-100">
                                <div className="text-xs text-gray-500 mb-1">{t('certificates.montantHt', { defaultValue: 'Montant HT' })}</div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {formatFcfa(load.amountHtExclTva ?? (Number(load.amountHt) - Number(load.amountTva || 0)))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                  ))}
                </div>
              </div>
            )}

                {selectedBill.billKind !== 'CREDIT_TOPUP' && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    {t('certificates.totals', { defaultValue: 'Totaux' })}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{t('certificates.montantHt', { defaultValue: 'Montant HT' })}</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">{formatFcfa(totalMontantHt)}</p>
                    </div>
                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{t('certificates.tva', { defaultValue: 'TVA' })}</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">{formatFcfa(totalTva)}</p>
                    </div>
                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">{t('certificates.montantTtc', { defaultValue: 'MONTANT TTC' })}</p>
                      <p className="text-lg font-semibold text-gray-900 mt-1">{formatFcfa(totalMontantTtc)}</p>
                    </div>
                  </div>
                </div>
                )}

                {Array.isArray(selectedBill.payments) && selectedBill.payments.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 border-b pb-2">{t('payments.title')}</h4>
                    <div className="space-y-2">
                      {selectedBill.payments.map((payment) => (
                        <div
                          key={payment.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
                        >
                          <div className="text-sm text-gray-900">
                            <span className="font-semibold">{t('payments.amount')}:</span>{' '}
                            {formatFcfa(payment.amount)}
                            <span className="ml-2 text-gray-600">
                              ({paymentMethodLabel(payment.paymentMethod)} -{' '}
                              {t(paymentStatusI18nKey(payment, selectedBill.payments), {
                                defaultValue: payment.status || '-',
                              })}
                              )
                            </span>
                          </div>
                          {!isPeseur() && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                              onClick={() => handleOpenRelatedPayment(payment.id)}
                            >
                              {t('bills.openRelatedPayment')}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-3">
              {canAccountantMarkPaidVerified(selectedBill) && (
                <Button
                  variant="outline"
                  onClick={() => openMarkPaidVerifiedModal(selectedBill)}
                  disabled={isLoading}
                  className="border-emerald-300 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                >
                  <ShieldCheckIcon className="h-4 w-4 mr-2" />
                  {t('bills.markPaidVerified')}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => handleDownloadPDF(selectedBill.id, selectedBill.billNumber)}
                disabled={isLoading}
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                {t('bills.downloadPdf')}
              </Button>
              {mailingEnabled && !isSuper() && !isPeseur() && (
                <Button
                  variant="outline"
                  onClick={() => handleSendEmail(selectedBill.id)}
                  disabled={isLoading}
                >
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  {t('bills.sendEmail')}
                </Button>
              )}
            </div>
                </>
              );
            })()}
          </div>
        </Modal>
      )}

      {/* Accountant: mark bill paid & verified — confirmation with bill details */}
      <Modal
        isOpen={showMarkPaidVerifiedModal}
        onClose={closeMarkPaidVerifiedModal}
        title={t('bills.markPaidVerifiedModalTitle')}
        size="lg"
        closeOnOverlayClick={!markPaidSubmitting}
        footer={
          <>
            <Button variant="outline" onClick={closeMarkPaidVerifiedModal} disabled={markPaidSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={executeMarkPaidVerified}
              loading={markPaidSubmitting}
              disabled={markPaidDetailLoading || !billForMarkPaid}
            >
              <span className="inline-flex items-center">
                <ShieldCheckIcon className="h-4 w-4 mr-2 shrink-0" />
                {t('bills.markPaidVerifiedConfirmAction')}
              </span>
            </Button>
          </>
        }
      >
        {markPaidDetailLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          (() => {
            const b = billMarkPaidDetail || billForMarkPaid;
            if (!b) return null;
            const payments = Array.isArray(b.payments) ? b.payments : [];
            const waitingCount = payments.filter((p) => p.status === 'WAITING_FOR_VERIFICATION').length;
            const fmtMoney = (v) =>
              new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(Number(v || 0));
            const paymentStatusLabel = (p) =>
              t(paymentStatusI18nKey(p, payments), { defaultValue: p.status });

            return (
              <div className="space-y-5">
                <p className="text-sm text-gray-600">{t('bills.markPaidVerifiedModalExplanation')}</p>

                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-3 text-sm">
                  <h4 className="font-semibold text-gray-900">{t('bills.billInformation')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <span className="text-gray-500">{t('bills.billNumber')}:</span>{' '}
                      <span className="font-mono font-medium">{b.billNumber}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('status')}:</span>{' '}
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadgeColor(b.status)}`}
                      >
                        {getBillStatusLabel(b)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('date')}:</span>{' '}
                      {b.date ? format(new Date(b.date), 'dd/MM/yyyy') : '—'}
                    </div>
                    <div>
                      <span className="text-gray-500">{t('bills.dueDate')}:</span>{' '}
                      {b.dueDate ? format(new Date(b.dueDate), 'dd/MM/yyyy') : '—'}
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-gray-500">{t('client')}:</span>{' '}
                      <span className="font-medium">{b.client?.companyName || '—'}</span>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-gray-500">{t('payments.amount')}:</span>{' '}
                      <span className="text-lg font-semibold text-green-700">{fmtMoney(b.totalAmount)}</span>
                    </div>
                    {b.certificate?.certificateNumber && (
                      <div className="sm:col-span-2">
                        <span className="text-gray-500">{t('certificates.certificateNumber')}:</span>{' '}
                        <span className="font-mono">{b.certificate.certificateNumber}</span>
                      </div>
                    )}
                    {b.billKind === 'CREDIT_TOPUP' && (
                      <div className="sm:col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                        {t('bills.markPaidVerifiedCreditTopupNote')}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{t('bills.markPaidVerifiedPaymentsSection')}</h4>
                  {payments.length === 0 ? (
                    <p className="text-sm text-gray-500">{t('bills.markPaidVerifiedNoPaymentsYet')}</p>
                  ) : (
                    <ul className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-48 overflow-y-auto">
                      {payments.map((p) => {
                        const verifierName = paymentVerifierDisplayName(p);
                        return (
                          <li key={p.id} className="px-3 py-2 text-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-medium tabular-nums">{fmtMoney(p.amount)}</span>
                              <span className="text-gray-600">{paymentMethodLabel(p.paymentMethod)}</span>
                              <span
                                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  p.status === 'WAITING_FOR_VERIFICATION'
                                    ? 'bg-orange-100 text-orange-800'
                                    : p.status === 'COMPLETED'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {paymentStatusLabel(p)}
                              </span>
                            </div>
                            {verifierName ? (
                              <div className="mt-1 text-xs text-gray-600">
                                <span className="text-gray-500">{t('payments.verifiedBy')}:</span>{' '}
                                <span className="font-medium text-gray-800">{verifierName}</span>
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {waitingCount > 0 && (
                    <p className="mt-2 text-xs text-orange-800">
                      {t('bills.markPaidVerifiedWaitingCount', { count: waitingCount })}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('bills.markPaidVerifiedSettlementLabel')}
                  </label>
                  <select
                    value={markPaidSettlementMethod}
                    onChange={(e) => setMarkPaidSettlementMethod(e.target.value)}
                    className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                    disabled={markPaidSubmitting}
                  >
                    <option value="CASH">{t('payments.methods.cash')}</option>
                    <option value="BANK_TRANSFER">{t('payments.methods.banktransfer')}</option>
                    <option value="WAVE">{t('payments.methods.wave')}</option>
                    <option value="ORANGE_MONEY">{t('payments.methods.orangeMoney')}</option>
                    <option value="CREDIT_CARD">{t('payments.methods.creditCard')}</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">{t('bills.markPaidVerifiedSettlementHint')}</p>
                </div>
              </div>
            );
          })()
        )}
      </Modal>

      {/* Payment Modal */}
      {selectedBill && showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedBill(null);
          }}
          bill={selectedBill}
          onPaymentSuccess={() => {
            setShowPaymentModal(false);
            setSelectedBill(null);
            refetchBills();
          }}
          toast={{ success, error }}
          t={t}
          paymentsAPI={paymentsAPI}
        />
      )}
    </div>
  );
};

// Payment Modal Component - Separate component with props
const PaymentModal = ({ isOpen, onClose, bill, onPaymentSuccess, toast, t, paymentsAPI }) => {
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: '',
    transactionId: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const isPayingClient = bill?.client?.billingType === 'PAYING';

  const paymentMethodOptions = React.useMemo(() => {
    const all = [
      { value: 'CASH', label: t('payments.methods.cash') },
      { value: 'CREDIT_CARD', label: t('payments.methods.creditCard') },
      { value: 'WAVE', label: t('payments.methods.wave') },
      { value: 'ORANGE_MONEY', label: t('payments.methods.orangeMoney') },
      { value: 'BANK_TRANSFER', label: t('payments.methods.banktransfer') },
      { value: 'CREDIT', label: t('payments.methods.credit') }
    ];
    if (isPayingClient) {
      return all.filter((m) => m.value !== 'CREDIT');
    }
    return all;
  }, [isPayingClient, t]);

  // Initialize payment amount when modal opens
  React.useEffect(() => {
    if (isOpen && bill) {
      setPaymentData({
        amount: bill.totalAmount.toFixed(2),
        paymentMethod: '',
        transactionId: ''
      });
    }
  }, [isOpen, bill]);

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentData.paymentMethod) {
      toast.error(t('payments.selectPaymentMethod'));
      return;
    }

    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error(t('payments.validAmountRequired'));
      return;
    }

    try {
      setIsProcessing(true);
      
      await paymentsAPI.create({
        billId: bill.id,
        amount: parseFloat(paymentData.amount),
        paymentMethod: paymentData.paymentMethod,
        transactionId: paymentData.transactionId || undefined
      });

      toast.success(t('payments.paymentProcessed'));
      onPaymentSuccess();
    } catch (err) {
      console.error('Payment error:', err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        t('payments.paymentFailed');
      toast.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!bill) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('payments.payBill')} - ${bill.billNumber}`}
      maxWidth="2xl"
    >
      <form onSubmit={handlePaymentSubmit} className="space-y-6">
        {/* Bill Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">{t('bills.billInformation')}</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><strong>{t('bills.billNumber')}:</strong> {bill.billNumber}</div>
            <div><strong>{t('client')}:</strong> {bill.client?.companyName}</div>
            <div>
              <strong>{t('loads.modal.ddpNumber')}:</strong>{' '}
              {bill.certificate?.ddpNumber ||
                bill.loads?.find((l) => l.ddp?.manutentionNumber)?.ddp?.manutentionNumber ||
                '-'}
            </div>
            <div><strong>{t('bills.dueDate')}:</strong> {format(new Date(bill.dueDate), 'dd/MM/yyyy')}</div>
            <div>
              <strong>{t('amount')}:</strong>
              <span className="ml-2 text-lg font-semibold text-green-600">
                {new Intl.NumberFormat('fr-FR', {
                  style: 'currency',
                  currency: 'XOF'
                }).format(bill.totalAmount)}
              </span>
            </div>
          </div>
          {isPayingClient && (
            <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {t('payments.payingClientNoCreditMethod')}
            </p>
          )}
        </div>

        {/* Payment Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('payments.amount')} *
            </label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={paymentData.amount}
              onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('payments.paymentMethod')} *
            </label>
            <select
              value={paymentData.paymentMethod}
              onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">{t('payments.selectPaymentMethod')}</option>
              {paymentMethodOptions.map((method) => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          {/* Transaction ID - Optional for electronic payments */}
          {(paymentData.paymentMethod === 'WAVE' || paymentData.paymentMethod === 'ORANGE_MONEY' || paymentData.paymentMethod === 'CREDIT_CARD') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('payments.transactionId')} ({t('common.optional')})
              </label>
              <Input
                type="text"
                value={paymentData.transactionId}
                onChange={(e) => setPaymentData(prev => ({ ...prev, transactionId: e.target.value }))}
                placeholder={t('payments.transactionIdPlaceholder')}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isProcessing}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {isProcessing ? t('common.loading') : t('payments.processPayment')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default BillsPage;